// InventoryCategoryServices.js
import { useState, useCallback } from "react";
import API from "../../api/apiAdmin";
import { useNotification } from "../../context/NotificationContext";

export function useInventoryCategories() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showNotification } = useNotification();

    const fetchAllCategories = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.get("/admin/inventory/categories/");
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to fetch categories.";
            setError(msg);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createCategory = useCallback(async (categoryData) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.post("/admin/inventory/categories/", categoryData);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Category Created",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create category.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const updateCategory = useCallback(async (id, categoryData) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.put(`/admin/inventory/categories/${id}`, categoryData);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Category Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to update category.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const deleteCategory = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.delete(`/admin/inventory/categories/${id}`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Category Deleted",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to delete category.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    const toggleCategoryStatus = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const res = await API.patch(`/admin/inventory/categories/${id}/toggle-status`);
            if (res.data.status === "success") {
                showNotification({
                    type: "success",
                    title: "Status Updated",
                    message: res.data.message,
                });
            }
            return res.data;
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to toggle status.";
            setError(msg);
            showNotification({
                type: "failed",
                title: "Error",
                message: msg,
            });
            return { status: "failed", message: msg };
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    return {
        loading,
        error,
        fetchAllCategories,
        createCategory,
        updateCategory,
        deleteCategory,
        toggleCategoryStatus
    };
}
